import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../UI/Icon'
import NewsCover from '../../../../UI/NewsCover'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import VisibilityAccess from '../../../VisibilityAccess'

import { types } from '../../../../shared/eventEmitter'
import { newsLocation, stateLabel } from '../../../../shared/helpers'
import { ADMIN_AREA_ROLES, TLanguages, TRoles } from '../../../../shared/const'

import styles from './style.module.scss'
import { formatVienna } from '../../../../shared/datetime'

/*
 * Nachricht ansehen.
 *
 * Zuerst der Text — das ist, was die Empfaenger:innen lesen. Die
 * Verteilungsangaben (Zielgruppen, Autor) stehen darunter und nur fuer Admins:
 * Fuer Schueler:innen und Lehrkraefte sind sie ohne Bedeutung.
 */

const EMPTY = { id: 0, title: '', description: '' }

const fmt = value => (value ? formatVienna(value, 'dd MMM RRRR') : null)
/*
 * Zwei Zeitpunkte als Spanne — aber nur, wenn es wirklich zwei sind. Eine
 * Veranstaltung an einem Tag stand sonst als „12 Nov 2026 – 12 Nov 2026" da.
 */
const spanne = (von, bis) => (von && bis && von !== bis ? `${von} – ${bis}` : von || bis || '')

function ViewFeedModal(props) {
	const { t, i18n } = useTranslation()
	const [feed, setFeed] = useState(EMPTY)

	const branchLabel = value => {
		const option = props.companyBranch?.find(v => v.value_EN === value)
		if (!option) return value

		return i18n.language === TLanguages.DE ? option.value_DE : option.value_EN
	}

	// Nur die gesetzten Einschraenkungen; leer heisst "an alle".
	const audience = [
		feed.roles?.length && { key: 'roles', label: t('User roles'), values: feed.roles.map(role => t(role)) },
		feed.states?.length && { key: 'states', label: t('state'), values: feed.states.map(stateLabel) },
		feed.schoolType?.length && { key: 'schoolType', label: t('School Types'), values: feed.schoolType },
		feed.companyTypes?.length && {
			key: 'companyTypes',
			label: t('Company type'),
			values: feed.companyTypes
		},
		feed.companyBranch?.length && {
			key: 'companyBranch',
			label: t('branch'),
			values: feed.companyBranch.map(branchLabel)
		}
	].filter(Boolean)

	/*
	 * Wie auf der Karte: Unter dem Titel steht der Termin der Veranstaltung
	 * (eventStart/eventEnd). Die Anzeigedauer (startDate/endDate) ist eine
	 * Angabe der Redaktion — an dieser Stelle gelesen wirkt sie wie ein Termin,
	 * den es nicht gibt. Die Redaktion bekommt sie unten bei den Zielgruppen.
	 */
	const isAdmin = props.role === TRoles.ADMIN || props.role === TRoles.FEDERAL_ADMIN

	const termin = spanne(fmt(feed.eventStart), fmt(feed.eventEnd))
	const anzeige = spanne(fmt(feed.startDate), fmt(feed.endDate))
	const ort = newsLocation(feed)

	return (
		<BaseModal onOpen={setFeed} nameEvent={types.openDetailFeedModal} title={t('Message')} maxWidth={640}>
			{() => (
				<div className={styles.root}>
					<div className={styles.badges}>
						{!!feed.newsType && (
							<span
								className={clsx(
									styles.badgeType,
									feed.newsType === 'EVENT' ? styles.badgeEvent : styles.badgeNews
								)}
							>
								{t(feed.newsType)}
							</span>
						)}
						<VisibilityAccess access={ADMIN_AREA_ROLES}>
							{!!feed.status && <span className={styles.badgeStatus}>{t(feed.status)}</span>}
						</VisibilityAccess>
					</div>

					{/* Titelbild über dem Text — dasselbe Bild und derselbe
					    Ausschnitt wie auf der Karte, von der aus geöffnet wurde. */}
					<NewsCover className={styles.cover} hashName={feed.imageHashName} alt={feed.title} />

					<h2 className={styles.title}>{feed.title}</h2>

					{(termin || ort) && (
						<p className={styles.meta}>
							{!!termin && (
								<span>
									{termin}
									{!!ort && <span className={styles.metaSeparator}>·</span>}
								</span>
							)}
							{!!ort && <span>{ort}</span>}
						</p>
					)}

					<p className={styles.description}>{feed.description}</p>

					{!!feed.externalLink && (
						<a
							className={styles.externalLink}
							href={feed.externalLink}
							target="_blank"
							rel="noopener noreferrer"
						>
							{t('External Link')}
							<Icon
								className={styles.externalLinkIcon}
								name="arrow"
								color="#008B9C"
								size={{ w: 8, h: 8 }}
							/>
						</a>
					)}

					<VisibilityAccess access={ADMIN_AREA_ROLES}>
						<div className={styles.distribution}>
							{/* Nur fuer die Redaktion — wie lange die Nachricht laeuft. */}
							{!!anzeige && (
								<p className={styles.display}>
									{t('Display period')}: {anzeige}
								</p>
							)}
							<h3 className={styles.distributionTitle}>{t('Target groups')}</h3>
							{audience.length ? (
								audience.map(item => (
									<div key={item.key} className={styles.facet}>
										<span className={styles.facetLabel}>{item.label}</span>
										<span className={styles.facetChips}>
											{item.values.map(value => (
												<span key={value} className={styles.facetChip}>
													{value}
												</span>
											))}
										</span>
									</div>
								))
							) : (
								<p className={styles.audienceAll}>{t('Visible to all users')}</p>
							)}
							{!!feed.creator?.email && (
								<p className={styles.author}>
									{t('Author')}: {feed.creator.email}
								</p>
							)}
						</div>
					</VisibilityAccess>
				</div>
			)}
		</BaseModal>
	)
}

ViewFeedModal.propTypes = {
	role: PropTypes.string,
	companyBranch: PropTypes.array
}

const mapStateToProps = state => ({
	role: state.user.role,
	companyBranch: state.options.branch
})

export default connect(mapStateToProps)(ViewFeedModal)
